/**
 * CO Swatch Filter
 *
 * Filters product swatches on collection pages based on the
 * data-co-colors attribute set on the parent product grid item.
 * This enables swatch splitting where products with many colors
 * are displayed across multiple cards.
 */

(function() {
  'use strict';

  const CO_DELIMITER = '§';
  const swatchIdentities = new WeakMap();
  let swatchInstance = 0;

  /**
   * Liquid dynamic blocks can repeat product/group IDs. Repair identity per DOM
   * container, including clones of already-normalized markup. This is a post-JS
   * repair only; initial scanning is document-wide, later insertions main-scoped.
   */
  function normalizeSwatchIdentity(container) {
    if (swatchIdentities.has(container)) return;

    const inputs = Array.from(container.querySelectorAll('.co-product-swatches__input'));
    if (!inputs.length) return;

    const labels = container.querySelectorAll('.co-product-swatches__label');
    const metadata = container.closest('[data-co-selected]');
    const color = metadata && metadata.getAttribute('data-co-selected');
    const selected = (color && inputs.find(function(input) {
      return (input.getAttribute('data-color') || '').toLowerCase() === color.toLowerCase();
    })) || inputs.find(function(input) { return input.checked; }) ||
      inputs.find(function(input) { return input.defaultChecked; });

    /* The marker cannot be emitted by a handleized color's single hyphens. */
    const stripInstance = function(value) { return value.replace(/--co-instance-\d+$/, ''); };
    const suffix = '--co-instance-' + (++swatchInstance);
    const name = stripInstance(inputs[0].getAttribute('name') || 'co-swatch') + suffix;

    inputs.forEach(function(input, index) {
      const oldId = input.getAttribute('id');
      const id = stripInstance(oldId || 'co-swatch-input-' + index) + suffix;
      input.setAttribute('name', name);
      input.setAttribute('id', id);
      labels.forEach(function(label) {
        if (label.getAttribute('for') === oldId) label.setAttribute('for', id);
      });
    });

    /* Rename the entire group before restoring parse-time-collapsed selection. */
    inputs.forEach(function(input) {
      input.checked = input === selected;
      labels.forEach(function(label) {
        if (label.getAttribute('for') !== input.id) return;
        if (input.checked) label.classList.add('co-product-swatches__label--selected');
        else label.classList.remove('co-product-swatches__label--selected');
      });
    });
    swatchIdentities.set(container, name);
  }

  function normalizeSwatchIdentities(root) {
    if (root.matches && root.matches('.co-product-swatches')) normalizeSwatchIdentity(root);
    if (root.querySelectorAll) root.querySelectorAll('.co-product-swatches').forEach(normalizeSwatchIdentity);
  }

  /**
   * Initialize swatch filtering for all product cards
   */
  function initSwatchFiltering() {
    normalizeSwatchIdentities(document);
    /* Find all product grid items with CO color data */
    const gridItems = document.querySelectorAll('[data-co-colors]');

    gridItems.forEach(function(gridItem) {
      filterSwatchesInCard(gridItem);
    });
  }

  /**
   * Synchronize product cards that have explicit split-card metadata or a
   * server-selected standard-product swatch.
   * @param {Document|HTMLElement} root - Root containing cards to synchronize
   */
  function syncVariantCards(root) {
    normalizeSwatchIdentities(root);
    var cards = new Set();
    var splitSelector = '[data-co-variant-image], [data-co-variant-url], [data-co-variant-price]';

    if (root.matches && root.matches(splitSelector)) {
      cards.add(root);
    }

    var splitCards = root.querySelectorAll ? root.querySelectorAll(splitSelector) : [];
    for (var i = 0; i < splitCards.length; i++) {
      cards.add(splitCards[i]);
    }

    if (root.matches && root.matches('.co-product-swatches__input:checked')) {
      var rootCard = root.closest('product-card') || root.closest('[data-product-id]');
      if (rootCard) cards.add(rootCard);
    }

    var selectedInputs = root.querySelectorAll ? root.querySelectorAll('.co-product-swatches__input:checked') : [];
    for (var j = 0; j < selectedInputs.length; j++) {
      var selectedCard = selectedInputs[j].closest('product-card') || selectedInputs[j].closest('[data-product-id]');
      if (selectedCard) cards.add(selectedCard);
    }

    for (const card of cards) {
      updateVariantCard(card);
    }
  }

  /**
   * Update product card images to show the server-selected variant image.
   */
  async function initVariantImages() {
    /* Correct server-selected card state as soon as the DOM is interactive so
     * shoppers never wait for every image/window load before seeing it. The
     * later pass remains authoritative after slideshow setup completes. */
    syncVariantCards(document);

    if (window.customElements) {
      await window.customElements.whenDefined('slideshow-component');
    }

    var syncAfterGallerySetup = function() {
      requestAnimationFrame(function() {
        syncVariantCards(document);
      });
    };

    if (document.readyState === 'complete') {
      syncAfterGallerySetup();
    } else {
      window.addEventListener('load', syncAfterGallerySetup, { once: true });
    }
  }

  /**
   * Synchronize all visible card state from its server-rendered variant data.
   * Used for both the initial page and cards inserted by AJAX/infinite scroll.
   * @param {HTMLElement} card - The product grid item element
   */
  function updateVariantCard(card) {
    var selectedColor = card.getAttribute('data-co-selected');
    var variantUrl = card.getAttribute('data-co-variant-url');
    var selectedInput = card.querySelector('.co-product-swatches__input:checked');

    if (!selectedInput && selectedColor) {
      var swatchInputs = card.querySelectorAll('.co-product-swatches__input');
      for (var i = 0; i < swatchInputs.length; i++) {
        if (swatchInputs[i].getAttribute('data-color') === selectedColor) {
          selectedInput = swatchInputs[i];
          break;
        }
      }
    }

    if (selectedInput) {
      selectedColor = selectedColor || selectedInput.getAttribute('data-color') || selectedInput.value;
      variantUrl = variantUrl || selectedInput.getAttribute('data-variant-url');
    }

    if (card.hasAttribute('data-co-variant-image')) {
      updateCardImage(card);
    } else if (selectedInput && variantUrl) {
      var variantId = selectedInput.getAttribute('data-variant-id') || card.getAttribute('data-co-display-variant-id');
      updateCardForVariant(card, variantId, selectedColor, variantUrl, selectedInput);
    }

    if (selectedColor) {
      updateColorLabel(card, selectedColor);
    }

    if (variantUrl) {
      updateCardLink(card, variantUrl);
    }

    updateCardPrice(card);
  }

  /**
   * Update the image for a single split card (legacy src-replacement approach)
   * Used for combined listings and as a fallback when filename filtering fails
   * @param {HTMLElement} card - The product grid item element
   */
  function updateCardImageLegacy(card) {
    var variantImageUrl = card.getAttribute('data-co-variant-image');
    var selectedColor = card.getAttribute('data-co-selected');
    if (!variantImageUrl) return;

    /* Try Aurora/Horizon theme structure first */
    var cardGallery = card.querySelector('.card-gallery');

    if (cardGallery) {
      /* Aurora/Horizon theme */
      var colorHandle = (selectedColor || '').toLowerCase()
        .replace(/\s*\/\s*/g, '-')
        .replace(/\s*\+\s*/g, '-')
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');

      /* Find all product media containers (slideshow slides) */
      var mediaContainers = cardGallery.querySelectorAll('.product-media-container, [class*="slideshow-slide"]');

      for (var i = 0; i < mediaContainers.length; i++) {
        var container = mediaContainers[i];
        var img = container.querySelector('img');
        if (!img) continue;

        var imgSrc = (img.getAttribute('src') || '').toLowerCase();

        /* Check if this image belongs to the selected variant */
        var isVariantImage = imgSrc.indexOf(colorHandle) !== -1 ||
                             imgSrc.indexOf(colorHandle.replace(/-/g, '')) !== -1;

        /* For the first slide, always update with the variant image */
        if (i === 0) {
          updateImageSrc(img, variantImageUrl);
          container.style.display = '';
          container.removeAttribute('hidden');
        } else if (isVariantImage) {
          container.style.display = '';
          container.removeAttribute('hidden');
        } else {
          container.style.display = 'none';
          container.setAttribute('hidden', '');
        }
      }

      /* Handle direct img elements not in containers */
      if (mediaContainers.length === 0) {
        var directImages = cardGallery.querySelectorAll('img.product-media__image');
        for (var j = 0; j < directImages.length; j++) {
          if (j === 0) {
            updateImageSrc(directImages[j], variantImageUrl);
          }
        }
      }
    } else {
      /* Legacy shopper theme - find images by class */
      var galleryContainer = card.querySelector('.product-gallery-images, .product-item-gallery, .product-image-wrapper');
      var images = galleryContainer ? galleryContainer.querySelectorAll('img.image, img.img-fluid') : card.querySelectorAll('img.image, img.img-fluid');

      if (images.length > 0) {
        /* Update the first/primary image */
        updateImageSrc(images[0], variantImageUrl);
      }
    }
  }

  /**
   * Update an image's src and srcset
   * @param {HTMLImageElement} img - The image element
   * @param {string} newUrl - The new image URL
   */
  function updateImageSrc(img, newUrl) {
    const currentSrc = img.getAttribute('src') || '';

    /* Extract filename from URLs for comparison */
    const getFilename = function(url) {
      if (!url) return '';
      const parts = url.split('/');
      const filename = parts[parts.length - 1];
      return filename.split('?')[0];
    };

    const currentFilename = getFilename(currentSrc);
    const newFilename = getFilename(newUrl);

    /* Only update if the image is different */
    if (currentFilename !== newFilename) {
      /* Generate srcset for responsive images */
      const baseUrl = newUrl.replace(/width=\d+/, 'width=');
      const srcset = [165, 360, 535, 750, 800].map(function(w) {
        return baseUrl.replace('width=', 'width=' + w) + ' ' + w + 'w';
      }).join(', ');

      img.setAttribute('src', newUrl);
      img.setAttribute('srcset', srcset);
    }
  }

  /**
   * Convert a color name to a URL-friendly handle
   * @param {string} color - The color name
   * @returns {string} The handleized color
   */
  function handleizeColor(color) {
    return (color || '').toLowerCase()
      .replace(/\s*\/\s*/g, '-')
      .replace(/\s*\+\s*/g, '-')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  /**
   * Extract the product handle from a card element's link
   * @param {HTMLElement} card - The product card element
   * @returns {string|null} The product handle
   */
  function extractProductHandle(card) {
    var link = card.querySelector('a[href*="/products/"]');
    if (link) {
      var match = link.getAttribute('href').match(/\/products\/([^?/]+)/);
      if (match) return match[1];
    }
    return null;
  }

  /**
   * Extract the product handle from a URL
   * @param {string} url - A product URL
   * @returns {string|null} The product handle
   */
  function extractHandleFromUrl(url) {
    if (!url) return null;
    var match = url.match(/\/products\/([^?/]+)/);
    return match ? match[1] : null;
  }

  /**
   * Filter gallery slides by color, showing only images whose filename contains
   * the color handle. This matches the live site behavior where images are
   * selected by filename convention (e.g., ".summit-stripe-denim.").
   *
   * @param {HTMLElement} card - The product card element
   * @param {string} color - The color name
   * @param {string} mediaId - Optional media ID for fallback navigation
   * @returns {boolean} True if slides were filtered and slideshow navigated
   */
  function filterGalleryByColor(card, color, mediaId) {
    var cardGallery = card.querySelector('.card-gallery');
    if (!cardGallery) return false;

    var colorHandle = handleizeColor(color);
    if (!colorHandle) return false;

    var slideshow = cardGallery.querySelector('slideshow-component');
    var slides = cardGallery.querySelectorAll('slideshow-slide');
    if (!slides.length) return false;

    var firstMatchIndex = -1;
    var firstMatchSlideId = null;
    var matchCount = 0;

    for (var i = 0; i < slides.length; i++) {
      var slide = slides[i];
      var img = slide.querySelector('img');
      if (!img) continue;

      var imgSrc = (img.getAttribute('src') || '').toLowerCase();

      /* Check if filename contains the color handle delimited by dots */
      var isMatch = imgSrc.indexOf('.' + colorHandle + '.') !== -1 ||
                    imgSrc.indexOf('.' + colorHandle.replace(/-/g, '') + '.') !== -1;

      if (isMatch) {
        slide.hidden = false;
        slide.style.display = '';
        slide.setAttribute('aria-hidden', 'false');
        matchCount++;
        if (firstMatchIndex === -1) {
          firstMatchIndex = i;
          firstMatchSlideId = slide.getAttribute('slide-id');
        }
      } else {
        slide.hidden = true;
        slide.style.display = 'none';
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    /* Fallback: if no filename matches, try media-id from swatch input */
    if (matchCount === 0 && mediaId) {
      var targetSlide = cardGallery.querySelector('slideshow-slide[slide-id="' + mediaId + '"]');
      if (targetSlide) {
        /* Show only this slide */
        for (var k = 0; k < slides.length; k++) {
          if (slides[k] === targetSlide) {
            slides[k].hidden = false;
            slides[k].style.display = '';
            slides[k].setAttribute('aria-hidden', 'false');
            matchCount = 1;
          } else {
            slides[k].hidden = true;
            slides[k].style.display = 'none';
            slides[k].setAttribute('aria-hidden', 'true');
          }
        }
      }
    }

    if (matchCount === 0) return false;

    /*
     * Reset the scroller position to show the first visible slide.
     * Hidden slides collapse (display:none), so the first matching slide
     * is at scrollLeft=0. We also sync slideshow.current to 0 so the
     * component's internal state matches.
     * Note: we avoid slideshow.select() because it early-returns when the
     * filtered slides[0] equals slides[current] (both index 0).
     */
    var scroller = slideshow ? slideshow.querySelector('[ref="scroller"]') : null;
    if (scroller) {
      scroller.scrollLeft = 0;
    }
    if (slideshow && typeof slideshow.current !== 'undefined') {
      slideshow.current = 0;
    }

    return true;
  }

  /**
   * Update the image for a split card — tries filename-based filtering first,
   * falls back to legacy src-replacement approach
   * @param {HTMLElement} card - The product grid item element
   */
  function updateCardImage(card) {
    var selectedColor = card.getAttribute('data-co-selected');

    /* Try filename-based filtering first */
    if (selectedColor && filterGalleryByColor(card, selectedColor, null)) {
      return;
    }

    /* Fall back to legacy approach */
    updateCardImageLegacy(card);
  }

  /**
   * Filter swatches within a single product card
   * @param {HTMLElement} gridItem - The product grid item element
   */
  function filterSwatchesInCard(gridItem) {
    /* Color filtering must never set checked while another card owns the name. */
    normalizeSwatchIdentities(gridItem);
    const coColorsAttr = gridItem.getAttribute('data-co-colors');
    const coSelectedAttr = gridItem.getAttribute('data-co-selected');

    if (!coColorsAttr) return;

    /* Parse the allowed colors */
    const allowedColors = coColorsAttr.split(CO_DELIMITER).map(function(c) {
      return c.trim().toLowerCase();
    });

    if (allowedColors.length === 0) return;

    /* Find swatches container within this card */
    const swatchesContainer = gridItem.querySelector('.co-product-swatches');
    if (!swatchesContainer) return;

    /* Find all swatch items */
    const swatchItems = swatchesContainer.querySelectorAll('.co-product-swatches__item');

    swatchItems.forEach(function(item) {
      const input = item.querySelector('input[data-color]');
      if (!input) return;

      const colorValue = (input.getAttribute('data-color') || '').toLowerCase();

      /* Hide swatches not in the allowed list */
      if (!allowedColors.includes(colorValue)) {
        item.style.display = 'none';
        item.setAttribute('aria-hidden', 'true');
      } else {
        item.style.display = '';
        item.removeAttribute('aria-hidden');

        /* Set selected state if this is the selected color */
        if (coSelectedAttr && colorValue === coSelectedAttr.toLowerCase()) {
          input.checked = true;
          const label = item.querySelector('.co-product-swatches__label');
          if (label) {
            label.classList.add('co-product-swatches__label--selected');
          }
        }
      }
    });
  }

  /**
   * Handle dynamic content (infinite scroll, AJAX)
   */
  function observeNewContent() {
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            normalizeSwatchIdentities(node);
            /* Check if the added node has CO colors */
            if (node.hasAttribute && node.hasAttribute('data-co-colors')) {
              filterSwatchesInCard(node);
            }
            /* Check children of the added node */
            const childItems = node.querySelectorAll && node.querySelectorAll('[data-co-colors]');
            if (childItems) {
              childItems.forEach(filterSwatchesInCard);
            }
            /* Synchronize split and standard cards added by section rendering. */
            syncVariantCards(node);
            /* Replace navigate handler on new product-card elements */
            var newCards = node.querySelectorAll && node.querySelectorAll('product-card');
            if (newCards) {
              newCards.forEach(replaceNavigateHandler);
            }
            if (node.matches && node.matches('product-card')) {
              replaceNavigateHandler(node);
            }
          }
        });
      });
    });

    /*
     * Observe the main storefront region rather than only collection grids.
     * Product recommendations render asynchronously outside `.product-grid`,
     * and their cards need the same initial state and click interception.
     */
    const storefrontRoot = document.querySelector('main') || document.body;
    if (storefrontRoot) {
      observer.observe(storefrontRoot, { childList: true, subtree: true });
    }
  }

  /**
   * Find the parent product card from a swatch element
   * @param {HTMLElement} swatchEl - A swatch input or label element
   * @returns {HTMLElement|null} The product card element
   */
  function findCardFromSwatch(swatchEl) {
    var swatchContainer = swatchEl.closest('.co-product-swatches, .product-option-swatch-wrapper, product-swatches, .variant-option');
    if (!swatchContainer) return null;

    var ancestor = swatchContainer;
    while (ancestor) {
      ancestor = ancestor.parentElement;
      if (!ancestor) break;
      if (ancestor.tagName === 'PRODUCT-SWATCHES') continue;
      if (ancestor.matches('product-card, .product-grid__item, .co-product-item') ||
          (ancestor.matches('[data-product-id]') && ancestor.querySelector('.card-gallery'))) {
        return ancestor;
      }
    }
    return null;
  }

  /**
   * Handle a swatch click — update the card image, label, and link
   * @param {HTMLInputElement} swatchInput - The swatch radio input
   * @param {HTMLElement} card - The product card element
   */
  function handleSwatchClick(swatchInput, card) {
    var variantUrl = swatchInput.getAttribute('data-variant-url') ||
                     swatchInput.getAttribute('data-connected-product-url');

    var variantId = swatchInput.getAttribute('data-variant-id') ||
                    swatchInput.getAttribute('data-first-available-or-first-variant-id');

    if (!variantId && variantUrl) {
      var variantMatch = variantUrl.match(/[?&]variant=(\d+)/);
      if (variantMatch) {
        variantId = variantMatch[1];
      }
    }

    var color = swatchInput.getAttribute('data-color') ||
                swatchInput.getAttribute('aria-label') ||
                swatchInput.value;

    if (!color) return;

    var productId = card.getAttribute('data-product-id');
    if (!productId) {
      var productIdEl = card.querySelector('[data-product-id]');
      if (productIdEl) {
        productId = productIdEl.getAttribute('data-product-id');
      }
    }

    if (variantUrl || (productId && variantId)) {
      syncVariantDataFromInput(card, swatchInput, color, variantId, variantUrl);
      updateCardForVariant(card, variantId, color, variantUrl, swatchInput);
    }

    updateSwatchSelectionGeneric(card, swatchInput);
  }

  /**
   * Return every element that stores card-level variant state.
   * @param {HTMLElement} card - The product card or grid item
   * @returns {HTMLElement[]} State-bearing card elements
   */
  function getCardStateElements(card) {
    var elements = [card];
    var gridItem = card.closest && card.closest('.product-grid__item, .co-product-item');
    var productCard = card.matches && card.matches('product-card') ? card : card.querySelector && card.querySelector('product-card');

    if (gridItem && elements.indexOf(gridItem) === -1) elements.push(gridItem);
    if (productCard && elements.indexOf(productCard) === -1) elements.push(productCard);

    return elements;
  }

  /**
   * Set or remove a data attribute on every card-state element.
   * @param {HTMLElement} card - The product card or grid item
   * @param {string} name - Attribute name
   * @param {string|null|undefined} value - Attribute value
   */
  function setCardDataAttribute(card, name, value) {
    getCardStateElements(card).forEach(function(element) {
      if (value === null || value === undefined || value === '') {
        element.removeAttribute(name);
      } else {
        element.setAttribute(name, value);
      }
    });
  }

  /**
   * Copy rendered variant data from a swatch input onto the card.
   * @param {HTMLElement} card - The product card or grid item
   * @param {HTMLInputElement} swatchInput - The selected swatch input
   * @param {string} color - Selected color
   * @param {string} variantId - Selected variant ID
   * @param {string} variantUrl - Selected variant URL
   */
  function syncVariantDataFromInput(card, swatchInput, color, variantId, variantUrl) {
    setCardDataAttribute(card, 'data-co-selected', color);
    setCardDataAttribute(card, 'data-co-display-variant-id', variantId);
    setCardDataAttribute(card, 'data-co-variant-url', variantUrl);
    setCardDataAttribute(card, 'data-co-variant-price', swatchInput.getAttribute('data-variant-price'));
    setCardDataAttribute(card, 'data-co-variant-compare-at-price', swatchInput.getAttribute('data-variant-compare-at-price'));
    updateCardPrice(card);
  }

  /**
   * Resolve an event target to the corresponding swatch input
   * @param {Element} target - The event target
   * @returns {HTMLInputElement|null} The swatch input, or null if not a swatch click
   */
  function resolveSwatchInput(target) {
    var label = target.closest('.co-product-swatches__label');
    var input = target.closest('.co-product-swatches__input');

    if (!label && !input) {
      label = target.closest('.color-swatch, .variant-option__button-label');
      input = target.closest('.color-swatch-input, input[type="radio"][name*="color"]');
    }

    /* Fallback: click hit the wrapper (.co-product-swatches__item) — look inside it */
    if (!label && !input) {
      var item = target.closest('.co-product-swatches__item');
      if (item) {
        input = item.querySelector('input[type="radio"]');
      }
    }

    if (!label && !input) return null;

    var swatchInput = input;
    if (!swatchInput && label) {
      swatchInput = label.previousElementSibling;
      if (!swatchInput || swatchInput.tagName !== 'INPUT') {
        swatchInput = label.querySelector('input[type="radio"]');
      }
      if (!swatchInput) {
        swatchInput = label.closest('.co-product-swatches__item, .color-swatch-item, .variant-option__swatch')?.querySelector('input[type="radio"]');
      }
    }

    if (!swatchInput || swatchInput.disabled) return null;
    return swatchInput;
  }

  /**
   * Initialize swatch click handlers to update card without navigation.
   *
   * Replaces the navigateToProduct event listener on each product-card
   * with a wrapper that intercepts swatch clicks. Also prevents
   * mousedown on swatches to stop the visually-hidden input from
   * scrolling into view (the "jump" bug).
   */
  function initSwatchClicks() {
    var cards = document.querySelectorAll('product-card');
    cards.forEach(function(card) {
      replaceNavigateHandler(card);
    });
  }

  /**
   * Intercept swatch clicks on a product-card before the built-in
   * navigateToProduct handler can trigger page navigation.
   *
   * Why capture phase + stopImmediatePropagation:
   *   product-card registers navigateToProduct in bubble phase via
   *   addEventListener('click', this.navigateToProduct). The bundler
   *   may wrap that reference, so removeEventListener with the property
   *   silently fails. A capture-phase handler fires BEFORE any bubble
   *   handler, and stopImmediatePropagation prevents every subsequent
   *   handler (capture or bubble, same element or descendants) from
   *   running. Non-swatch clicks pass through untouched.
   *
   * @param {HTMLElement} card - A product-card element
   */
  function replaceNavigateHandler(card) {
    if (card._coNavReplaced) return;
    card._coNavReplaced = true;

    /* Capture-phase click: intercept swatch clicks before navigateToProduct */
    card.addEventListener('click', function(event) {
      if (!(event.target instanceof Element)) return;

      var swatchEl = event.target.closest('.co-product-swatches');
      if (!swatchEl) return; /* Not a swatch — let the original handler run */

      /* Swatch click — stop everything and handle the colour switch */
      event.stopImmediatePropagation();

      var swatchInput = resolveSwatchInput(event.target);
      /* Cancel label forwarding, but not native radio activation: canceling an
       * input click rolls its checked state back after keyboard activation. */
      if (!swatchInput || event.target !== swatchInput) event.preventDefault();
      if (swatchInput) {
        swatchInput.checked = true;
        handleSwatchClick(swatchInput, card);
      }
    }, true); /* ← capture phase */

    /*
     * Capture-phase mousedown: prevent the browser from scrolling to
     * the visually-hidden radio input (the "jump" bug) and stop the
     * slideshow drag handler from capturing the pointer.
     */
    card.addEventListener('mousedown', function(e) {
      if (e.target instanceof Element && e.target.closest('.co-product-swatches')) {
        e.preventDefault();
      }
    }, true);
  }

  /**
   * Update swatch selection styling for any swatch system
   * @param {HTMLElement} card - The product card element
   * @param {HTMLInputElement} selectedInput - The newly selected input
   */
  function updateSwatchSelectionGeneric(card, selectedInput) {
    /* Find the swatch container */
    const swatchContainer = selectedInput.closest('.co-product-swatches, .product-option-swatch-wrapper, product-swatches, .color-swatch-container');
    if (!swatchContainer) return;

    /* Remove selection from all inputs and labels */
    const allInputs = swatchContainer.querySelectorAll('input[type="radio"]');
    allInputs.forEach(function(input) {
      input.checked = false;
      const label = input.nextElementSibling || input.closest('label') || swatchContainer.querySelector('label[for="' + input.id + '"]');
      if (label) {
        label.classList.remove('co-product-swatches__label--selected', 'active', 'is-selected', 'selected');
      }
    });

    /* Select the new swatch */
    selectedInput.checked = true;
    const selectedLabel = selectedInput.nextElementSibling ||
                          selectedInput.closest('label') ||
                          swatchContainer.querySelector('label[for="' + selectedInput.id + '"]');
    if (selectedLabel) {
      selectedLabel.classList.add('co-product-swatches__label--selected');
      /* Also add common active classes in case the styling uses them */
      if (selectedLabel.classList.contains('color-swatch')) {
        selectedLabel.classList.add('active');
      }
    }
  }

  /**
   * Update swatch selection styling
   * @param {HTMLElement} card - The product card element
   * @param {HTMLInputElement} selectedInput - The newly selected input
   */
  function updateSwatchSelection(card, selectedInput) {
    /* Remove selection from all swatches in this card */
    const swatchContainer = card.querySelector('.co-product-swatches');
    if (!swatchContainer) return;

    const allLabels = swatchContainer.querySelectorAll('.co-product-swatches__label');
    allLabels.forEach(function(label) {
      label.classList.remove('co-product-swatches__label--selected');
    });

    const allInputs = swatchContainer.querySelectorAll('.co-product-swatches__input');
    allInputs.forEach(function(input) {
      input.checked = false;
    });

    /* Select the new swatch */
    selectedInput.checked = true;
    const selectedLabel = selectedInput.nextElementSibling;
    if (selectedLabel) {
      selectedLabel.classList.add('co-product-swatches__label--selected');
    }
  }

  /**
   * Update card content for a new variant
   * @param {HTMLElement} card - The product card element
   * @param {string} variantId - The variant ID
   * @param {string} color - The color name
   * @param {string} variantUrl - The variant URL
   * @param {HTMLInputElement} swatchInput - The swatch input element
   */
  function updateCardForVariant(card, variantId, color, variantUrl, swatchInput) {
    /* Check if this is a combined listing (swatch points to a different product) */
    var currentProductHandle = extractProductHandle(card);
    var swatchProductHandle = extractHandleFromUrl(variantUrl);
    var isCombinedListing = (swatchInput && swatchInput.getAttribute('data-is-combined-listing') === 'true') ||
                            (swatchProductHandle && currentProductHandle && swatchProductHandle !== currentProductHandle);

    if (isCombinedListing) {
      /*
       * Combined listing: always fetch the child product. The card's product ID
       * belongs to the combined parent and its cached JSON can select a sibling's
       * first image when the child swatch intentionally has no local variant ID.
       */
      fetchVariantDetails(null, swatchProductHandle, variantId, function(details) {
        if (details.imageUrl) {
          setCardDataAttribute(card, 'data-co-variant-image', details.imageUrl);
          updateCardImageLegacy(card);
        }
        applyVariantPrice(card, details.price, details.compareAtPrice);
        updateColorLabel(card, color);
        updateCardLink(card, variantUrl);
      });
    } else {
      /* Regular product: filter gallery slides by color (no API call) */
      var mediaId = swatchInput ? swatchInput.getAttribute('data-media-id') : null;

      var filtered = filterGalleryByColor(card, color, mediaId);

      if (!filtered) {
        /* Fallback: fetch variant image and replace src */
        var productHandle = extractHandleFromUrl(variantUrl) || extractProductHandle(card);
        var pid = card.getAttribute('data-product-id');
        fetchVariantImage(pid, productHandle, variantId, function(imageUrl) {
          if (imageUrl) {
            setCardDataAttribute(card, 'data-co-variant-image', imageUrl);
            updateCardImageLegacy(card);
          }
        });
      }

      updateColorLabel(card, color);
      updateCardLink(card, variantUrl);
    }
  }

  /**
   * Apply a variant price to the visible card.
   * @param {HTMLElement} card - The product card or grid item
   * @param {string} price - Formatted price
   * @param {string} compareAtPrice - Formatted compare-at price
   */
  function applyVariantPrice(card, price, compareAtPrice) {
    setCardDataAttribute(card, 'data-co-variant-price', price);
    setCardDataAttribute(card, 'data-co-variant-compare-at-price', compareAtPrice);
    updateCardPrice(card);
  }

  /**
   * Format Shopify cents using the theme money format.
   * @param {number|string|null|undefined} cents - Amount in cents
   * @returns {string} Formatted money
   */
  function formatVariantMoney(cents) {
    if (cents === null || cents === undefined || cents === '') return '';
    var format = window.theme && window.theme.moneyFormat;
    if (window.CO && typeof window.CO.formatMoney === 'function') {
      return window.CO.formatMoney(cents, format);
    }
    var amount = Number(cents) / 100;
    if (Number.isNaN(amount)) return '';
    return '$' + amount.toFixed(2);
  }

  /**
   * Normalize product JSON into the card details needed by swatches.
   * @param {Object} product - Shopify product JSON
   * @param {string} variantId - Variant ID
   * @returns {{imageUrl: string|null, price: string, compareAtPrice: string}}
   */
  function buildVariantDetails(product, variantId) {
    var variants = product && product.variants ? product.variants : [];
    var variant = variantId ? variants.find(function(v) { return v.id == variantId; }) : null;

    if (!variant) {
      variant = variants.find(function(v) { return v.available; }) || variants[0] || null;
    }

    var imageUrl = null;
    if (variant && variant.featured_image) {
      imageUrl = variant.featured_image.src || variant.featured_image;
    } else if (product && product.featured_image) {
      imageUrl = product.featured_image;
    } else if (product && product.images && product.images.length > 0) {
      imageUrl = product.images[0];
    }

    var price = variant ? formatVariantMoney(variant.price) : '';
    var compareAtPrice = '';
    if (variant && variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price)) {
      compareAtPrice = formatVariantMoney(variant.compare_at_price);
    }

    return { imageUrl: imageUrl, price: price, compareAtPrice: compareAtPrice };
  }

  /**
   * Fetch variant image, price, and compare-at price.
   * @param {string} productId - The product ID (numeric)
   * @param {string} productHandle - The product handle (URL-friendly name)
   * @param {string} variantId - The variant ID
   * @param {function} callback - Callback with variant details
   */
  function fetchVariantDetails(productId, productHandle, variantId, callback) {
    /* Try to get from product JSON on page first */
    if (productId) {
      var productJson = document.querySelector('[data-product-json-' + productId + ']');
      if (productJson) {
        try {
          var data = JSON.parse(productJson.textContent);
          if (data && data.variants) {
            callback(buildVariantDetails(data, variantId));
            return;
          }
        } catch (e) { /* ignore */ }
      }
    }

    /* Fetch from Shopify API using product handle (more reliable than numeric ID) */
    var fetchPath = productHandle ? '/products/' + productHandle + '.js' : '/products/' + productId + '.js';
    fetch(fetchPath)
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Product fetch failed');
        }
        return response.json();
      })
      .then(function(product) {
        callback(buildVariantDetails(product, variantId));
      })
      .catch(function() {
        callback({ imageUrl: null, price: '', compareAtPrice: '' });
      });
  }

  /**
   * Fetch variant image URL.
   * @param {string} productId - The product ID (numeric)
   * @param {string} productHandle - The product handle (URL-friendly name)
   * @param {string} variantId - The variant ID
   * @param {function} callback - Callback with image URL
   */
  function fetchVariantImage(productId, productHandle, variantId, callback) {
    fetchVariantDetails(productId, productHandle, variantId, function(details) {
      callback(details.imageUrl);
    });
  }

  /**
   * Update the color label text on a card
   * @param {HTMLElement} card - The product card element
   * @param {string} color - The new color name
   */
  function updateColorLabel(card, color) {
    /* Find color label elements - try various selectors */
    var selectors = [
      '.custom-product-variant-color',
      '.co-product-item__color',
      '[class*="product-color"]',
      '.product-card__color',
      'product-title [ref="subtitle"]',
      '.product-item-style-title-option'  /* Legacy shopper theme */
    ];

    for (var i = 0; i < selectors.length; i++) {
      var label = card.querySelector(selectors[i]);
      if (label) {
        label.textContent = color;
        return;
      }
    }

    /* Try to find subtitle in product-title component */
    var productTitle = card.querySelector('product-title');
    if (productTitle) {
      var subtitle = productTitle.querySelector('[ref="subtitle"]');
      if (subtitle) {
        subtitle.textContent = color;
      }
    }
  }

  /**
   * Update the visible card price from server-rendered CO variant data.
   * @param {HTMLElement} card - The product card or grid item
   */
  function updateCardPrice(card) {
    var price = card.getAttribute('data-co-variant-price');
    if (!price) return;

    var compareAtPrice = card.getAttribute('data-co-variant-compare-at-price');
    var priceContainer = card.querySelector('product-price [ref="priceContainer"], [ref="priceContainer"]');
    if (!priceContainer) return;

    var priceEl = priceContainer.querySelector('.price');
    if (priceEl) {
      priceEl.textContent = price;
    }

    var compareEl = priceContainer.querySelector('.compare-at-price');
    if (compareAtPrice) {
      priceContainer.classList.add('price--on-sale');
      if (compareEl) {
        compareEl.textContent = compareAtPrice;
        var compareGroup = compareEl.closest('[role="group"]');
        if (compareGroup) compareGroup.style.display = '';
      }
    } else {
      priceContainer.classList.remove('price--on-sale');
      if (compareEl) {
        var group = compareEl.closest('[role="group"]');
        if (group) {
          group.style.display = 'none';
        } else {
          compareEl.textContent = '';
        }
      }
    }
  }

  /**
   * Update the card's link URL
   * @param {HTMLElement} card - The product card element
   * @param {string} url - The new URL
   */
  function updateCardLink(card, url) {
    if (!url) return;

    /* Update all links in the card */
    const links = card.querySelectorAll('a[href*="/products/"]');
    links.forEach(function(link) {
      /* If the URL already contains a collection path, use it as-is */
      if (url.includes('/collections/')) {
        link.setAttribute('href', url);
        return;
      }
      /* Otherwise, preserve the collection context from the current link */
      const currentHref = link.getAttribute('href');
      if (currentHref && currentHref.includes('/collections/')) {
        const collectionMatch = currentHref.match(/\/collections\/[^\/]+/);
        if (collectionMatch) {
          link.setAttribute('href', collectionMatch[0] + url);
          return;
        }
      }
      link.setAttribute('href', url);
    });
  }

  /**
   * Run all initialization functions
   */
  function init() {
    initSwatchFiltering();
    initVariantImages();
    initSwatchClicks();
    observeNewContent();
  }

  /* Initialize on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
